import { useEffect, useRef, useState } from "react";

/**
 * Animated film-grain overlay (PostHog-inspired).
 *
 * Perf:
 * - Disabled on reduced-motion + small/touch screens
 * - Pauses when tab is hidden (Page Visibility API)
 * - Reseeds at ~10fps with rAF — no React state per frame
 * - `contain: strict` + `pointer-events: none` keeps it off the hit-test path
 */
export const GrainOverlay = () => {
  const ref = useRef<SVGFETurbulenceElement>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduceMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
    const tinyMQ = window.matchMedia("(pointer: coarse) and (max-width: 768px)");
    const compute = () => setEnabled(!reduceMQ.matches && !tinyMQ.matches);
    compute();
    reduceMQ.addEventListener?.("change", compute);
    tinyMQ.addEventListener?.("change", compute);
    return () => {
      reduceMQ.removeEventListener?.("change", compute);
      tinyMQ.removeEventListener?.("change", compute);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let last = 0;
    const FREQ = 1000 / 10;
    let running = true;

    const tick = (t: number) => {
      if (!running) return;
      if (t - last > FREQ && ref.current) {
        last = t;
        ref.current.setAttribute("seed", String(Math.floor(Math.random() * 1000)));
      }
      raf = requestAnimationFrame(tick);
    };
    const start = () => {
      if (running) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(tick);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };
    const onVis = () => (document.hidden ? stop() : start());

    raf = requestAnimationFrame(tick);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled]);

  return (
    <div
      aria-hidden="true"
      role="presentation"
      className="pointer-events-none fixed inset-0 z-[55] mix-blend-overlay opacity-[0.085] motion-reduce:hidden"
      style={{ contain: "strict" }}
    >
      <svg className="size-full" xmlns="http://www.w3.org/2000/svg">
        <filter id="lov-grain">
          <feTurbulence
            ref={ref}
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves="2"
            stitchTiles="stitch"
            seed="2"
          />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0 0
                    0 0 0 0.65 0"
          />
        </filter>
        <rect width="100%" height="100%" filter="url(#lov-grain)" />
      </svg>
    </div>
  );
};
